import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { StatCard, Section, PageHeader } from "@/components/dashboard/DashboardPrimitives";

export default function SchoolAdminDashboard({ user }) {
  const [school, setSchool] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [stats, setStats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [mutating, setMutating] = useState(false);

  const load = async () => {
    try {
      const [p, t, s, i, a] = await Promise.all([
        api.get("/api/school/profile"),
        api.get("/api/school/teachers"),
        api.get("/api/school/stats"),
        api.get("/api/auth/teacher-invites"),
        api.get("/api/school/recent-activity"),
      ]);
      setSchool(p.data.school);
      setForm(p.data.school);
      setTeachers(t.data.teachers);
      setStats(s.data);
      setInvites(i.data.invites);
      setActivity(a.data.activity);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generateInvite = async () => {
    setMutating(true);
    try {
      await api.post("/api/auth/teacher-invite", { label: null });
      const i = await api.get("/api/auth/teacher-invites");
      setInvites(i.data.invites);
      const s = await api.get("/api/school/stats");
      setStats(s.data);
    } catch (e) {
      alert(formatApiError(e));
    } finally { setMutating(false); }
  };

  const revokeInvite = async (code) => {
    if (!window.confirm(`Revoke invite ${code}?`)) return;
    try {
      await api.delete(`/api/school/teacher-invite/${code}`);
      const i = await api.get("/api/auth/teacher-invites");
      setInvites(i.data.invites);
    } catch (e) { alert(formatApiError(e)); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setMutating(true);
    try {
      const { data } = await api.patch("/api/school/profile", {
        school_name: form.school_name,
        school_address: form.school_address,
        school_board: form.school_board,
      });
      setSchool(data.school);
      setEditing(false);
    } catch (e2) {
      alert(formatApiError(e2));
    } finally { setMutating(false); }
  };

  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <>
      <PageHeader
        user={user}
        subtitle="Manage teachers, view aggregated class growth data, brand reports with your logo."
      />

      {stats && (
        <Section title="Your school at a glance" testid="school-stats">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total reports" value={stats.total_reports} accent="sky" />
            <StatCard label="Reports (30d)" value={stats.reports_last_30d} />
            <StatCard label="Teachers" value={stats.teacher_count} accent="emerald" />
            <StatCard label="Pending invites" value={stats.pending_invites} accent="amber" />
          </div>
        </Section>
      )}

      <Section
        title="School profile"
        right={
          !editing ? (
            <button onClick={() => { setForm(school); setEditing(true); }}
              data-testid="school-edit-button"
              className="text-sm px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">
              Edit
            </button>
          ) : null
        }
        testid="school-profile-section"
      >
        <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-6">
          <div className="flex items-start gap-5">
            {school.school_logo_path && (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/auth/logo/${school.school_logo_path}`}
                alt="logo"
                className="w-24 h-24 rounded-lg bg-slate-900 object-contain border border-slate-800"
              />
            )}
            <div className="flex-1 min-w-0">
              {editing ? (
                <form onSubmit={saveProfile} className="space-y-3" data-testid="school-edit-form">
                  <input
                    className="w-full px-3 py-2 rounded bg-[#0B1428] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none"
                    value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                    placeholder="School name" data-testid="school-name-input"
                  />
                  <input
                    className="w-full px-3 py-2 rounded bg-[#0B1428] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none"
                    value={form.school_address} onChange={(e) => setForm({ ...form, school_address: e.target.value })}
                    placeholder="Address" data-testid="school-address-input"
                  />
                  <select
                    className="w-full px-3 py-2 rounded bg-[#0B1428] border border-slate-700 text-slate-100 outline-none"
                    value={form.school_board} onChange={(e) => setForm({ ...form, school_board: e.target.value })}
                    data-testid="school-board-select"
                  >
                    {["CBSE","ICSE","State","IB","Other"].map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <button type="submit" disabled={mutating}
                      className="px-4 py-1.5 rounded bg-sky-500 hover:bg-sky-400 text-white text-sm disabled:opacity-60"
                      data-testid="school-save-button">Save</button>
                    <button type="button" onClick={() => setEditing(false)}
                      className="px-4 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm">Cancel</button>
                  </div>
                </form>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-slate-100">{school.school_name}</h3>
                  <p className="text-sm text-slate-400 mt-1">{school.school_address}</p>
                  <p className="text-xs text-slate-500 mt-2">Board: {school.school_board}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="Recent activity"
        description="Last 20 reports generated within your school — aggregated, no individual medical data"
        testid="school-activity-section"
      >
        <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 divide-y divide-slate-800/80">
          {activity.length === 0 ? (
            <p className="p-6 text-sm text-slate-400" data-testid="school-activity-empty">
              No reports generated yet. Once your teachers run analyses, activity will appear here.
            </p>
          ) : activity.map(a => (
            <div key={a.report_id} className="p-3 flex items-center gap-3 text-sm" data-testid={`activity-row-${a.report_id}`}>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded w-24 text-center flex-shrink-0 ${
                a.report_type === "boneah" ? "bg-sky-500/10 text-sky-300" : "bg-emerald-500/10 text-emerald-300"
              }`}>
                {a.report_type === "boneah" ? "BoneAH" : "NutriGuide"}
              </span>
              <span className="text-slate-300 flex-1 truncate">by {a.author_name}</span>
              <span className="text-xs text-slate-500 flex-shrink-0">
                {new Date(a.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Teachers"
        description={`${teachers.length} joined`}
        testid="school-teachers-section"
        right={
          <button onClick={generateInvite} disabled={mutating}
            data-testid="generate-invite-button"
            className="px-4 py-1.5 text-sm rounded bg-sky-500 hover:bg-sky-400 text-white disabled:opacity-60">
            {mutating ? "…" : "Generate invite code"}
          </button>
        }
      >
        <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 divide-y divide-slate-800/80 mb-3">
          {teachers.length === 0 && <p className="p-6 text-sm text-slate-400">No teachers yet — generate an invite code.</p>}
          {teachers.map(t => (
            <div key={t.user_id} className="p-4" data-testid={`teacher-row-${t.user_id}`}>
              <p className="text-sm font-medium text-slate-100">{t.full_name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t.email} · {t.class_grade || "—"}</p>
            </div>
          ))}
        </div>
        {invites.length > 0 && (
          <>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Invite codes</p>
            <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 divide-y divide-slate-800/80">
              {invites.map(inv => (
                <div key={inv.code} className="p-3 flex items-center justify-between" data-testid={`invite-row-${inv.code}`}>
                  <div>
                    <code className="font-mono text-base tracking-widest text-sky-300">{inv.code}</code>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {inv.used ? "Used" : "Unused"} · Expires {inv.expires_at?.slice(0, 10)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigator.clipboard?.writeText(inv.code)}
                      className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">Copy</button>
                    {!inv.used && (
                      <button onClick={() => revokeInvite(inv.code)}
                        data-testid={`invite-revoke-${inv.code}`}
                        className="text-xs px-2 py-1 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border border-rose-500/30">
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>
    </>
  );
}
